// DurationInput.jsx — the one control for entering a service duration.
//
// Renders a number field plus a compact unit toggle (hrs / days). ALWAYS calls
// onChange with a decimal number of DAYS, whichever unit is showing, so no
// parent's state shape, payload key, or validation has to change.
//
// All the arithmetic lives in util/durationDisplay.js and is unit-tested there.
// Keep this file thin.

import React, { useState } from 'react';
import { Form, InputGroup, Button } from 'react-bootstrap';
import {
  MAX_ENTRY_HOURS,
  DAY_STEP,
  initialUnit,
  toInputValue,
  fromInputValue,
} from '../../util/durationDisplay';

export default function DurationInput({
  value,
  onChange,
  isInvalid = false,
  required = false,
  readOnly = false,
  plaintext = false,
  hint = false,
  // 'right' sits beside the field; 'below' drops it under and turns the arrow
  // upward. Narrow columns must pass 'below' — a left arrow on a wrapped hint
  // points at empty space.
  hintPlacement = 'right',
  name,
  id,
}) {
  // Seeded once from the incoming value. If a single mounted instance can be
  // handed a DIFFERENT service's duration, give it a `key` at the call site so
  // it remounts and re-seeds.
  const [unit, setUnit] = useState(() => initialUnit(value));

  const shown = toInputValue(value, unit);

  const handleChange = (e) => onChange(fromInputValue(e.target.value, unit));

  const toggleUnit = () => setUnit((u) => (u === 'hours' ? 'days' : 'hours'));

  return (
    <div className="d-flex align-items-center flex-wrap" style={{ gap: '0.5rem' }}>
      <InputGroup style={{ flex: '0 1 180px', width: 'auto' }}>
        <Form.Control
          type="number"
          name={name}
          id={id}
          value={shown}
          onChange={handleChange}
          isInvalid={isInvalid}
          required={required}
          readOnly={readOnly}
          plaintext={plaintext}
          min={1}
          max={unit === 'hours' ? MAX_ENTRY_HOURS : undefined}
          step={unit === 'hours' ? 1 : DAY_STEP}
          placeholder={unit === 'hours' ? '4' : '1'}
        />
        <InputGroup.Append>
          {readOnly ? (
            <InputGroup.Text>{unit === 'hours' ? 'hrs' : 'days'}</InputGroup.Text>
          ) : (
            <Button
              variant="outline-secondary"
              type="button"
              onClick={toggleUnit}
              // Under a day you enter hours; at a day or more you enter days.
              title="Switch between hours and days"
            >
              {unit === 'hours' ? 'hrs' : 'days'}
            </Button>
          )}
        </InputGroup.Append>
      </InputGroup>
      {/* The unit button does not read as clickable on its own, so the arrow
          points back at it — left when the hint sits beside the field, up when
          the call site is too narrow and puts it underneath. */}
      {hint && !readOnly && (
        <small className={hintPlacement === 'below' ? 'text-muted w-100' : 'text-muted text-nowrap'}>
          <i
            className={`fa-sharp fa-regular mr-1 ${hintPlacement === 'below' ? 'fa-arrow-up' : 'fa-arrow-left'}`}
            aria-hidden="true"
          />
          Click to change time unit
        </small>
      )}
    </div>
  );
}
